/* eslint-disable @typescript-eslint/no-explicit-any -- Supabase JSON/RPC + composer boundary; see mem://preferences/lint-debt.md */
import { supabase } from "@/integrations/supabase/client";

export type CorpusBucket = "primary" | "secondary" | "supplemental";

export const BUCKET_LABELS: Record<CorpusBucket, string> = {
  primary: "Primary",
  secondary: "Secondary",
  supplemental: "Supplemental",
};

export const BUCKET_ORDER: CorpusBucket[] = ["primary", "secondary", "supplemental"];

export type CorpusFolder = {
  id: string;
  workspace_id: string;
  parent_folder_id: string | null;
  name: string;
  bucket: CorpusBucket;
  position: number;
  exclude_from_grounding: boolean;
  created_at: string;
  updated_at: string;
};

export type CorpusEmbeddingStatus = "pending" | "processing" | "ready" | "stale" | "error";

export type CorpusDocumentRow = {
  id: string;
  workspace_id: string;
  folder_id: string | null;
  title: string;
  body_preview: string | null;
  source_kind: "paste" | "manual";
  position: number;
  embedding_status: CorpusEmbeddingStatus;
  chunk_count: number;
  embedding_refreshed_at: string | null;
  last_embedding_error: string | null;
  body_hash: string | null;
  created_at: string;
  updated_at: string;
};

const TBL_FOLDERS = "workspace_corpus_folders" as const;
const TBL_DOCS = "workspace_corpus_documents" as const;

// ---------------- Folders ----------------

export async function listFolders(workspaceId: string): Promise<CorpusFolder[]> {
  const { data, error } = await (supabase as any)
    .from(TBL_FOLDERS)
    .select("*")
    .eq("workspace_id", workspaceId)
    .order("bucket", { ascending: true })
    .order("position", { ascending: true })
    .order("created_at", { ascending: true });
  if (error) throw error;
  return (data ?? []) as CorpusFolder[];
}

export async function createFolder(input: {
  workspaceId: string;
  bucket: CorpusBucket;
  name: string;
  parentFolderId?: string | null;
}): Promise<CorpusFolder> {
  const { data, error } = await (supabase as any)
    .from(TBL_FOLDERS)
    .insert({
      workspace_id: input.workspaceId,
      bucket: input.bucket,
      name: input.name.trim() || "Untitled folder",
      parent_folder_id: input.parentFolderId ?? null,
    })
    .select("*")
    .single();
  if (error) throw error;
  return data as CorpusFolder;
}

export async function renameFolder(folderId: string, name: string): Promise<void> {
  const { error } = await (supabase as any)
    .from(TBL_FOLDERS)
    .update({ name: name.trim() || "Untitled folder" })
    .eq("id", folderId);
  if (error) throw error;
}

export async function deleteFolder(folderId: string): Promise<void> {
  const { error } = await (supabase as any).from(TBL_FOLDERS).delete().eq("id", folderId);
  if (error) throw error;
}

/**
 * Reorder / re-parent a folder. Renumbers `position` for siblings in the
 * destination parent so the moved folder sits at `newIndex`. Caller is
 * responsible for guarding cross-bucket moves and self/descendant cycles.
 */
export async function reorderFolder(input: {
  folderId: string;
  newParentId: string | null;
  bucket: CorpusBucket;
  workspaceId: string;
  newIndex: number;
}): Promise<void> {
  const { folderId, newParentId, bucket, workspaceId, newIndex } = input;
  const { data: existing, error: fetchErr } = await (supabase as any)
    .from(TBL_FOLDERS)
    .select("id, parent_folder_id, bucket, position")
    .eq("workspace_id", workspaceId)
    .eq("bucket", bucket);
  if (fetchErr) throw fetchErr;
  const rows = (existing ?? []) as { id: string; parent_folder_id: string | null; position: number }[];
  // Siblings in destination, excluding the moved folder.
  const dest = rows
    .filter((r) => r.parent_folder_id === newParentId && r.id !== folderId)
    .sort((a, b) => a.position - b.position);
  const clamped = Math.max(0, Math.min(newIndex, dest.length));
  dest.splice(clamped, 0, { id: folderId, parent_folder_id: newParentId, position: 0 });
  // Move the folder first (parent reassignment), then renumber siblings.
  const { error: mvErr } = await (supabase as any)
    .from(TBL_FOLDERS)
    .update({ parent_folder_id: newParentId })
    .eq("id", folderId);
  if (mvErr) throw mvErr;
  // Renumber sequentially.
  for (let i = 0; i < dest.length; i++) {
    const r = dest[i];
    const { error } = await (supabase as any)
      .from(TBL_FOLDERS)
      .update({ position: i })
      .eq("id", r.id);
    if (error) throw error;
  }
}

/**
 * Reorder a document within its folder, or move it to a different folder
 * (folderId = null → Unfiled). Renumbers `position` across siblings in the
 * destination folder.
 */
export async function reorderDocument(input: {
  documentId: string;
  newFolderId: string | null;
  workspaceId: string;
  newIndex: number;
}): Promise<void> {
  const { documentId, newFolderId, workspaceId, newIndex } = input;
  const { data: existing, error: fetchErr } = await (supabase as any)
    .from(TBL_DOCS)
    .select("id, folder_id, position")
    .eq("workspace_id", workspaceId);
  if (fetchErr) throw fetchErr;
  const rows = (existing ?? []) as { id: string; folder_id: string | null; position: number }[];
  const dest = rows
    .filter((r) => r.folder_id === newFolderId && r.id !== documentId)
    .sort((a, b) => a.position - b.position);
  const clamped = Math.max(0, Math.min(newIndex, dest.length));
  dest.splice(clamped, 0, { id: documentId, folder_id: newFolderId, position: 0 });
  const { error: mvErr } = await (supabase as any)
    .from(TBL_DOCS)
    .update({ folder_id: newFolderId })
    .eq("id", documentId);
  if (mvErr) throw mvErr;
  for (let i = 0; i < dest.length; i++) {
    const r = dest[i];
    const { error } = await (supabase as any)
      .from(TBL_DOCS)
      .update({ position: i })
      .eq("id", r.id);
    if (error) throw error;
  }
}

// ---------------- Documents ----------------

export async function listDocuments(workspaceId: string): Promise<CorpusDocumentRow[]> {
  const { data, error } = await (supabase as any)
    .from(TBL_DOCS)
    .select(
      "id, workspace_id, folder_id, title, body_preview, source_kind, position, embedding_status, chunk_count, embedding_refreshed_at, last_embedding_error, body_hash, created_at, updated_at"
    )
    .eq("workspace_id", workspaceId)
    .order("position", { ascending: true })
    .order("created_at", { ascending: true });
  if (error) throw error;
  return (data ?? []) as CorpusDocumentRow[];
}


export async function setFolderExcludeFromGrounding(
  folderId: string,
  exclude: boolean
): Promise<void> {
  const { error } = await (supabase as any)
    .from(TBL_FOLDERS)
    .update({ exclude_from_grounding: exclude })
    .eq("id", folderId);
  if (error) throw error;
}

export async function createDocument(input: {
  workspaceId: string;
  folderId: string | null;
  title: string;
  body: string;
  sourceKind?: "paste" | "manual";
}): Promise<CorpusDocumentRow> {
  const { data, error } = await (supabase as any)
    .from(TBL_DOCS)
    .insert({
      workspace_id: input.workspaceId,
      folder_id: input.folderId,
      title: input.title.trim() || "Untitled",
      source_kind: input.sourceKind ?? "manual",
    })
    .select("*")
    .single();
  if (error) throw error;
  const row = data as CorpusDocumentRow;
  if (input.body && input.body.trim().length > 0) {
    const { error: bodyErr } = await (supabase as any).rpc(
      "set_workspace_corpus_document_body",
      { _document_id: row.id, _body: input.body }
    );
    if (bodyErr) throw bodyErr;
    // Kick off embedding (fire-and-forget). Swallow rejection so it never
    // hits the global unhandled-rejection handler / ErrorBoundary; notify
    // any registered UI handler instead.
    void invokeCorpusEmbed(row.id).catch((err) => {
      notifyCorpusEmbedError(err);
    });
  }
  return row;
}

type CorpusEmbedErrorHandler = (err: unknown) => void;
let corpusEmbedErrorHandler: CorpusEmbedErrorHandler | null = null;

export function setCorpusEmbedErrorHandler(fn: CorpusEmbedErrorHandler | null): void {
  corpusEmbedErrorHandler = fn;
}

function notifyCorpusEmbedError(err: unknown): void {
  try {
    corpusEmbedErrorHandler?.(err);
  } catch {
    // never let the handler itself throw
  }
}

export async function updateDocument(input: {
  id: string;
  title?: string;
  body?: string;
  folderId?: string | null;
}): Promise<void> {
  const patch: Record<string, unknown> = {};
  if (input.title !== undefined) patch.title = input.title.trim() || "Untitled";
  if (input.folderId !== undefined) patch.folder_id = input.folderId;
  if (Object.keys(patch).length > 0) {
    const { error } = await (supabase as any).from(TBL_DOCS).update(patch).eq("id", input.id);
    if (error) throw error;
  }
  if (input.body !== undefined) {
    const { error: bodyErr } = await (supabase as any).rpc(
      "set_workspace_corpus_document_body",
      { _document_id: input.id, _body: input.body }
    );
    if (bodyErr) throw bodyErr;
    // Mark stale; user can manually re-embed (auto re-embed fires from the
    // hook that observes idle time after edit).
    await (supabase as any)
      .from(TBL_DOCS)
      .update({ embedding_status: "stale" })
      .eq("id", input.id)
      .in("embedding_status", ["ready", "stale"]);
  }
}

export async function invokeCorpusEmbed(documentId: string): Promise<void> {
  const { error } = await supabase.functions.invoke("corpus-embed", {
    body: { document_id: documentId },
  });
  if (error) throw error;
}

export async function invokeCorpusEmbedBatch(
  workspaceId: string,
  mode: "all" | "stale"
): Promise<void> {
  const { error } = await supabase.functions.invoke("corpus-embed", {
    body: { workspace_id: workspaceId, mode },
  });
  if (error) throw error;
}

export async function deleteDocument(id: string): Promise<void> {
  const { error } = await (supabase as any).from(TBL_DOCS).delete().eq("id", id);
  if (error) throw error;
}

export async function getDocumentBody(id: string): Promise<string> {
  const { data, error } = await (supabase as any).rpc(
    "get_decrypted_workspace_corpus_document",
    { _document_id: id }
  );
  if (error) throw error;
  return (data as string | null) ?? "";
}

// ---------------- Topic chips (Codex Framework Topics) ----------------

const TBL_DOC_TOPICS = "workspace_corpus_document_topics" as const;

/**
 * Returns a map of documentId → array of framework_topic_ids for the given
 * workspace. Used by the dock to render chips on rows and to power the
 * Topic filter strip above the document list.
 */
export async function listDocumentTopics(
  workspaceId: string
): Promise<Record<string, string[]>> {
  const { data, error } = await (supabase as any)
    .from(TBL_DOC_TOPICS)
    .select("document_id, framework_topic_id")
    .eq("workspace_id", workspaceId);
  if (error) throw error;
  const out: Record<string, string[]> = {};
  for (const row of (data ?? []) as { document_id: string; framework_topic_id: string }[]) {
    if (!out[row.document_id]) out[row.document_id] = [];
    out[row.document_id].push(row.framework_topic_id);
  }
  return out;
}

/**
 * Replace the set of Topic assignments on a single document. Idempotent —
 * computes the diff and only inserts/deletes what changed.
 */
export async function setDocumentTopics(
  documentId: string,
  workspaceId: string,
  topicIds: string[]
): Promise<void> {
  const next = Array.from(new Set(topicIds));
  const { data: existing, error: fetchErr } = await (supabase as any)
    .from(TBL_DOC_TOPICS)
    .select("framework_topic_id")
    .eq("document_id", documentId);
  if (fetchErr) throw fetchErr;
  const current = new Set(
    ((existing ?? []) as { framework_topic_id: string }[]).map((r) => r.framework_topic_id)
  );
  const toAdd = next.filter((t) => !current.has(t));
  const toRemove = Array.from(current).filter((t) => !next.includes(t));

  if (toAdd.length > 0) {
    const rows = toAdd.map((framework_topic_id) => ({
      document_id: documentId,
      workspace_id: workspaceId,
      framework_topic_id,
    }));
    const { error } = await (supabase as any).from(TBL_DOC_TOPICS).insert(rows);
    if (error) throw error;
  }
  if (toRemove.length > 0) {
    const { error } = await (supabase as any)
      .from(TBL_DOC_TOPICS)
      .delete()
      .eq("document_id", documentId)
      .in("framework_topic_id", toRemove);
    if (error) throw error;
  }
}